import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import moment from "moment";
import { useTranslation } from "react-i18next";

/** Stavy spracovania externej objednávky. */
const STATUS_ORDER = ["ready", "inProgress", "done"];

const statusLabel = (t, status) => {
  switch (status) {
    case "ready":
      return t("pages.dsokReports.externalOrdersStatus.ready", {
        defaultValue: "Čaká na vyzdvihnutie",
      });
    case "inProgress":
      return t("pages.dsokReports.externalOrdersStatus.inProgress", {
        defaultValue: "Spracúva sa",
      });
    case "done":
      return t("pages.dsokReports.externalOrdersStatus.done", {
        defaultValue: "Vyzdvihnuté",
      });
    default:
      return status || "—";
  }
};

const statusColor = (status) => {
  switch (status) {
    case "ready":
      return "warning";
    case "inProgress":
      return "info";
    case "done":
      return "success";
    default:
      return "default";
  }
};

const formatMoney = (value, currency) => {
  const num = typeof value === "number" ? value : parseFloat(value);
  if (!Number.isFinite(num)) return "";
  return `${num.toFixed(2)}${currency ? ` ${currency}` : ""}`;
};

const orderNumberOf = (o) =>
  o?.externalOrderName ||
  (o?.externalOrderNumber != null ? `#${o.externalOrderNumber}` : "") ||
  o?.externalOrderId ||
  "—";

const customerNameOf = (o) => {
  const c = o?.customer || {};
  const name = `${c.firstName || ""} ${c.lastName || ""}`.trim();
  return name || c.email || "—";
};

const ExternalOrdersReport = ({ data }) => {
  const { t } = useTranslation();
  const orders = Array.isArray(data?.orders) ? data.orders : [];
  const statusCounts = data?.statusCounts || {};
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return orders;
    return orders.filter((o) => o?.processStatus === statusFilter);
  }, [orders, statusFilter]);

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setPage(0);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}
      >
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="external-orders-status-filter">
            {t("pages.dsokReports.externalOrdersStatusFilter", {
              defaultValue: "Stav objednávky",
            })}
          </InputLabel>
          <Select
            labelId="external-orders-status-filter"
            label={t("pages.dsokReports.externalOrdersStatusFilter", {
              defaultValue: "Stav objednávky",
            })}
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            <MenuItem value="all">
              {t("pages.dsokReports.externalOrdersStatus.all", {
                defaultValue: "Všetky",
              })}
            </MenuItem>
            {STATUS_ORDER.map((s) => (
              <MenuItem key={s} value={s}>
                {statusLabel(t, s)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
          <Chip
            label={`${t("pages.dsokReports.externalOrdersTotal", {
              defaultValue: "Spolu",
            })}: ${orders.length}`}
            variant="outlined"
          />
          {STATUS_ORDER.map((s) =>
            statusCounts[s] ? (
              <Chip
                key={s}
                color={statusColor(s)}
                label={`${statusLabel(t, s)}: ${statusCounts[s]}`}
                variant="outlined"
              />
            ) : null,
          )}
        </Stack>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {t("pages.dsokReports.externalOrdersColOrderNo", {
                  defaultValue: "Číslo objednávky",
                })}
              </TableCell>
              <TableCell>
                {t("pages.dsokReports.externalOrdersColDate", {
                  defaultValue: "Dátum",
                })}
              </TableCell>
              <TableCell>
                {t("pages.dsokReports.externalOrdersColCustomer", {
                  defaultValue: "Zákazník",
                })}
              </TableCell>
              <TableCell align="right">
                {t("pages.dsokReports.externalOrdersColTotal", {
                  defaultValue: "Suma",
                })}
              </TableCell>
              <TableCell>
                {t("pages.dsokReports.externalOrdersColStatus", {
                  defaultValue: "Stav",
                })}
              </TableCell>
              <TableCell align="center">
                {t("pages.dsokReports.externalOrdersColLink", {
                  defaultValue: "Odkaz",
                })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {t("pages.dsokReports.externalOrdersEmpty", {
                      defaultValue: "Žiadne objednávky pre zvolený filter.",
                    })}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paged.map((o) => {
                const currency = o?.prices?.currency || o?.currency || "";
                const link = o?.integrationMeta?.orderStatusUrl;
                return (
                  <TableRow key={o?._id || o?.externalOrderId || orderNumberOf(o)}>
                    <TableCell>{orderNumberOf(o)}</TableCell>
                    <TableCell>
                      {o?.createdAt
                        ? moment(o.createdAt).format("DD.MM.YYYY HH:mm")
                        : "—"}
                    </TableCell>
                    <TableCell>{customerNameOf(o)}</TableCell>
                    <TableCell align="right">
                      {formatMoney(o?.prices?.totalPrice, currency) || "—"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={statusColor(o?.processStatus)}
                        label={statusLabel(t, o?.processStatus)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {link ? (
                        <Link
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: "inline-flex", alignItems: "center" }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </Link>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t("pages.dsokReports.externalOrdersRowsPerPage", {
            defaultValue: "Riadkov na stranu",
          })}
        />
      </TableContainer>
    </Box>
  );
};

export default ExternalOrdersReport;
